/*
  Execution site mode (09 #8) — the phone's outbox. Ticks and photos made without signal wait here and are sent
  when the connection is back (on the browser's "online" event, when the page becomes visible, on a retry timer
  while something waits, and right after each new tick). Every operation has an op_id, so a resend after a lost
  answer is harmless (the server stores it once).

  - ticks: localStorage `sara.site.<projectId>.<userId>.ticks` (small JSON; survives a reload, a closed tab and a
    sign-in again) — per person, so a phone shared by two people never sends one's ticks under the other's login.
    The stored list is the truth: every change reads it again first, so two queues on it at once (another tab, or
    React StrictMode mounting twice in development) never drop each other's ticks; without storage they live in
    memory
  - photos: IndexedDB `sara-site` › `photos` (blobs, tagged with project and person); without IndexedDB (old
    browsers, tests) they wait in memory for as long as the page is open

  What happens to a waiting op when the server answers:
  - no answer (no signal), 401 (the session ended — after signing in again the page sends them), 408, 429 and
    every 5xx (the server restarts or is busy): the op is KEPT and retried with backoff (30 s, 1, 2, 4, 5 min);
  - 400 / 403 / 404 / 409 / 413 / 422: the server refused that op for good — it is dropped and the reason shown.

  createSiteQueue({ projectId, userId, post, upload, onChange }) →
    { enqueueCheck({ taskId, checked }), enqueuePhoto({ taskId, file }), flush(), pendingTicks(), status(), dispose() }
  post(checks) → the server's { applied, skipped, tasks }; upload({ taskId, file, opId }) → the photo response.
  flush() → { applied, skipped (each with task_id), tasks, photos: [{ taskId, photos }] } of what was answered.
*/
import { readStored, removeStored, storageKey, writeStored } from '@/lib/storage';

const DB_NAME = 'sara-site';
const STORE = 'photos';
export const RETRY_MS = 30000;
export const MAX_RETRY_MS = 5 * 60000;
const FINAL_STATUSES = new Set([400, 403, 404, 409, 413, 422]);

export const newOpId = () => `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** No answer at all (no signal, the request timed out) — keep the op for later. */
export const isOfflineError = (err) => !err?.response;

/** The server refused this op for good (bad request, no access, the item is gone…) — drop it. Anything else
 *  (no answer, 401 session ended, 408, 429, 5xx) is kept and sent again later. */
export const isFinalRefusal = (err) => Boolean(err?.response) && FINAL_STATUSES.has(Number(err.response.status));

const refusalText = (err, fallback) => (typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : fallback);
const waitingText = (err) => {
  const code = Number(err?.response?.status);
  if (code === 401) return 'Signed out — sign in again to send';
  if (code === 429) return 'The server is busy — sends by itself';
  if (code === 408) return 'No answer in time — sends by itself';
  if (code >= 500) return 'The server is not answering — sends by itself';
  return '';
};

function openDb() {
  if (typeof indexedDB === 'undefined' || !indexedDB?.open) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'opId' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

const tx = (db, mode, run) => new Promise((resolve) => {
  try {
    const transaction = db.transaction(STORE, mode);
    const result = run(transaction.objectStore(STORE));
    transaction.oncomplete = () => resolve(result?.result ?? true);
    transaction.onerror = () => resolve(null);
  } catch {
    resolve(null);
  }
});

export function createSiteQueue({ projectId, userId = '', post, upload, onChange = () => {} }) {
  const owner = String(userId || 'anon');
  const tickKey = storageKey('site', projectId, owner, 'ticks');
  const validOps = (list) => (Array.isArray(list) ? list.filter((op) => op && op.op_id && op.task_id) : []);
  const probeKey = storageKey('site', 'probe');
  const storageOk = writeStored(probeKey, 1);
  if (storageOk) removeStored(probeKey);
  let memoryTicks = validOps(readStored(tickKey, []));
  const getTicks = () => (storageOk ? validOps(readStored(tickKey, [])) : memoryTicks);
  const setTicks = (list) => {
    memoryTicks = list;
    if (!storageOk) return;
    if (list.length) writeStored(tickKey, list);
    else removeStored(tickKey);
  };
  let memoryPhotos = [];
  let dbPromise = null;
  let flushing = null;
  let timer = null;
  let disposed = false;
  let lastError = ''; // a refusal (dropped ops)
  let waiting = ''; // why waiting ops were not sent yet (signed out, server busy)
  let failures = 0;

  const db = () => {
    if (!dbPromise) dbPromise = openDb();
    return dbPromise;
  };
  const mine = (entry) => entry.projectId === projectId && String(entry.userId || 'anon') === owner;
  const photosOfProject = async () => {
    const database = await db();
    if (!database) return memoryPhotos.filter(mine);
    const all = await tx(database, 'readonly', (store) => store.getAll());
    return (Array.isArray(all) ? all : []).filter(mine);
  };
  const removePhoto = async (opId) => {
    memoryPhotos = memoryPhotos.filter((entry) => entry.opId !== opId);
    const database = await db();
    if (database) await tx(database, 'readwrite', (store) => store.delete(opId));
  };
  const status = async () => {
    const photos = await photosOfProject();
    return {
      ticks: getTicks().length,
      photos: photos.length,
      error: lastError,
      waiting,
      online: typeof navigator === 'undefined' || navigator.onLine !== false,
    };
  };
  const notify = async (extra = {}) => {
    if (disposed) return;
    onChange({ ...(await status()), ...extra });
  };
  const retryDelay = () => Math.min(MAX_RETRY_MS, RETRY_MS * (2 ** Math.max(0, failures - 1)));
  const schedule = () => {
    if (timer || disposed) return;
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, failures ? retryDelay() : RETRY_MS);
  };

  async function flush() {
    if (flushing) return flushing;
    flushing = (async () => {
      const result = { applied: [], skipped: [], tasks: [], photos: [] };
      let kept = false;
      let keptReason = '';
      const batch = getTicks().slice(0, 500);
      if (batch.length) {
        const sentIds = new Set(batch.map((op) => op.op_id));
        try {
          const response = await post(batch);
          const taskOf = new Map(batch.map((op) => [op.op_id, op.task_id]));
          const skipped = (response?.skipped || []).map((entry) => ({ ...entry, task_id: entry.task_id || taskOf.get(entry.op_id) }));
          const done = new Set([...(response?.applied || []), ...skipped.map((entry) => entry.op_id)]);
          setTicks(getTicks().filter((op) => !done.has(op.op_id))); // read again: a tick made meanwhile stays
          result.applied.push(...(response?.applied || []));
          result.skipped.push(...skipped);
          result.tasks.push(...(response?.tasks || []));
        } catch (err) {
          if (isFinalRefusal(err)) {
            // refused for good (e.g. no edit access): drop the batch, say why
            setTicks(getTicks().filter((op) => !sentIds.has(op.op_id)));
            lastError = refusalText(err, 'The server refused the ticks');
          } else {
            kept = true; // no signal, signed out, server restarting or busy: keep them and try again later
            keptReason = waitingText(err);
          }
        }
      }
      if (!kept) {
        for (const entry of await photosOfProject()) {
          try {
            const response = await upload({ taskId: entry.taskId, file: entry.file, opId: entry.opId });
            result.photos.push({ taskId: entry.taskId, photos: response?.photos || [] });
            await removePhoto(entry.opId);
          } catch (err) {
            if (isFinalRefusal(err)) {
              await removePhoto(entry.opId);
              lastError = refusalText(err, 'A photo was refused');
            } else {
              kept = true;
              keptReason = waitingText(err);
              break;
            }
          }
        }
      }
      failures = kept ? failures + 1 : 0;
      waiting = kept ? keptReason : '';
      const left = await status();
      if (left.ticks || left.photos) schedule();
      await notify({ result });
      return result;
    })();
    try {
      return await flushing;
    } finally {
      flushing = null;
    }
  }

  const onOnline = () => { void flush(); };
  const onVisible = () => { if (typeof document !== 'undefined' && document.visibilityState === 'visible') void flush(); };
  if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);

  return {
    enqueueCheck({ taskId, checked }) {
      // a newer op for the same device replaces the waiting one
      setTicks([...getTicks().filter((op) => op.task_id !== taskId), { op_id: newOpId(), task_id: taskId, checked: Boolean(checked), at: new Date().toISOString() }]);
      lastError = '';
      void notify();
    },
    async enqueuePhoto({ taskId, file }) {
      const entry = { opId: newOpId(), projectId, userId: owner, taskId, file, name: file?.name || 'photo.jpg', at: new Date().toISOString() };
      const database = await db();
      if (database) {
        const saved = await tx(database, 'readwrite', (store) => store.put(entry));
        if (saved === null) memoryPhotos.push(entry);
      } else {
        memoryPhotos.push(entry);
      }
      lastError = '';
      await notify();
      return entry.opId;
    },
    /** Ticks still waiting: Map(taskId → checked) — the page shows them as ticked already. */
    pendingTicks: () => new Map(getTicks().map((op) => [op.task_id, op.checked])),
    status,
    flush,
    dispose() {
      disposed = true;
      if (timer) clearTimeout(timer);
      if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    },
  };
}
